import { Dashboard, UserBadge, Admin, IdInfo } from "@rsuite/icons";
import OthersIcon from "@rsuite/icons/Others";
import MediaIcon from "@rsuite/icons/Media";
import PeopleSpeakerIcon from "@rsuite/icons/PeopleSpeaker";
import TextImageIcon from "@rsuite/icons/TextImage";
import CalendarIcon from "@rsuite/icons/Calendar";
import EventDetailIcon from "@rsuite/icons/EventDetail";
import DetailIcon from "@rsuite/icons/Detail";
import PlayOutlineIcon from "@rsuite/icons/PlayOutline";

import { useTranslation } from "react-i18next";
import { any, isEmpty } from "lodash/fp";
import {
  availableOnDayObjArr,
  foodTypeObjArr,
  IS_ALPHANUMERIC_REGEX,
  IS_ALPHANUMERIC_WITH_DASH_REGEX,
  IS_ALPHANUMERIC_WITH_DASH_SPACE_REGEX,
  IS_ALPHANUMERIC_WITH_SPACE_REGEX,
  IS_ALPHA_REGEX,
  sizeObjArr,
  statusObjArr,
  orderDayTypeObj,
  dateFormatObj,
  onOffArr,
  yesNoObj,
} from "../core/config/AppConstants";
import moment from "moment";
function isEmptyVal(value: any) {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" && !value && Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  );
}

function isValidURL(url) {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
}

function getBasename(url) {
  try {
    const parsedUrl = new URL(url);
    const path = parsedUrl.pathname;
    const basename = path.substring(path.lastIndexOf("/") + 1);
    return basename;
  } catch (e) {
    console.error("Invalid URL");
    return null;
  }
}

function isNotEmpty(value: any) {
  if (value === null || value === undefined) return false;
  if (typeof value === "object") return Object.keys(value).length > 0;
  if (typeof value === "string")
    return (
      value.trim().length > 0 &&
      value.trim() !== "null" &&
      value.trim() !== "undefined"
    );
  return value !== undefined && value !== null;
}

function isNotNull(value: any) {
  return value !== null && value !== undefined;
}

function removeHTMLTags(input: string): string {
  return input.replace(/<[^>]*>/g, ""); // This regular expression matches any HTML tag and removes it
}

function displayField(value: any) {
  return isNotEmpty(value) ? value : "-NA-";
}

function countCommaSeparatedVals(vals) {
  if (!vals) return 0; // Handle empty or null input
  return vals
    .split(",")
    .map((val) => val.trim()) // Trim extra spaces
    .filter((val) => val !== "").length; // Remove empty values (if any)
}

function displayChartCompany(chartData: any) {
  let val = "-NA-";
  if (isNotEmpty(chartData.for_all_company)) val = "-All-";
  else if (isNotEmpty(chartData.company_name)) val = chartData.company_name;
  return val;
}

function displayAmountField(value: any) {
  return isNotEmpty(value) ? parseFloat(value).toFixed(2) : "0.00";
}

function zeroFill(value: string, len: number) {
  const lng = value.length;
  let i: number = 0;
  let zero = "";
  if (!lng) return value;
  if (lng < len) {
    for (i = 0; i < len - lng; i++) {
      zero = zero + "0";
    }
  }
  return zero + value;
}

function processMinute(value: number): string {
  let processed = "";

  const hours = Math.trunc(value / 60);
  const minutes = value % 60;

  if (hours > 0) {
    processed += `${hours} Hr${hours > 1 ? "s" : ""}. `;
  }

  processed += `${minutes} Min${minutes > 1 ? "s" : ""}.`;

  return processed.trim();
}

function processSecond(value: number): string {
  let processed = "";

  const hours = Math.trunc(value / 3600);
  const minutes = Math.trunc((value % 3600) / 60);
  const seconds = value % 60;

  if (hours > 0) {
    processed += `${hours} Hr${hours > 1 ? "s" : ""}. `;
  }
  if (minutes > 0) {
    processed += `${minutes} Min${minutes > 1 ? "s" : ""}. `;
  }
  if (seconds > 0) {
    processed += `${seconds} Sec${seconds > 1 ? "s" : ""}.`;
  }

  return processed.trim();
}

function displayDateField(
  value: any,
  displayFormat = dateFormatObj.display_d_m_y,
  localFormat = true
) {
  if (isEmptyVal(value)) return "-NA-";
  // Assuming MySQL date format is 'YYYY-MM-DD HH:mm:ss'
  const utcMoment =
    localFormat === true
      ? moment.utc(value, "YYYY-MM-DD HH:mm:ss")
      : moment(value, "YYYY-MM-DD HH:mm:ss");

  if (utcMoment.isValid()) {
    // Convert to local time zone
    const localMoment = utcMoment.local();
    // Format the date according to the desired format
    return localFormat === true
      ? localMoment.format(displayFormat)
      : utcMoment.format(displayFormat);
  } else {
    console.error("Invalid MySQL date format");
    return "-NA-";
  }
}

function displayDayDateField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_day_d_m_y
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayDateTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_d_m_y_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayYesNo(value: any) {
  return value == onOffArr.on ? yesNoObj.yes : yesNoObj.no;
}

function formatTimeField(mysqlTime, format = "24hr", withSec = true) {
  if (!mysqlTime) return ""; // Handle invalid input

  const [hours, minutes, seconds] = mysqlTime.split(":").map(Number);
  let returnTime = "";

  if (format === "12hr") {
    const period = hours >= 12 ? "PM" : "AM";
    const hour12 = hours % 12 || 12;
    returnTime = `${hour12}:${String(minutes).padStart(2, "0")}`;
    if (withSec) returnTime += `:${String(seconds).padStart(2, "0")}`;
    returnTime += ` ${period}`;
    return returnTime;
  } else {
    returnTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
    if (withSec) returnTime += `:${String(seconds).padStart(2, "0")}`;
    return returnTime;
  }
}

function generateTimeOptions(interval: number = 5) {
  const times = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += interval) {
      // Format values for 24-hour format
      const hour24 = String(h).padStart(2, "0");
      const minute = String(m).padStart(2, "0");

      // Convert to 12-hour format with AM/PM
      const period = h < 12 ? "AM" : "PM";
      const hour12 = String(h % 12 === 0 ? 12 : h % 12).padStart(2, "0"); // Ensuring 2-digit format

      times.push({
        label: `${hour12}:${minute} ${period}`, // Display in 12-hour format (zero-padded)
        value: `${hour24}:${minute}`, // Keep value in 24-hour format
      });
    }
  }
  return times;
}

function getTodayShortDay() {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();
  return days[today.getDay()];
}

function getDateShortDay(inputDate: any) {
  const days = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const date = new Date(inputDate);
  return days[date.getDay()];
}

function displayTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayDayDateTimeField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_day_d_m_y_h_m
) {
  return displayDateField(value, displayFormat, localFormat);
}

function displayDateMonthField(
  value: any,
  localFormat = true,
  displayFormat = dateFormatObj.display_date_month
) {
  return displayDateField(value, displayFormat, localFormat);
}

function addLabelValue(
  valArr: any,
  lableIndex: string,
  valueIndex: string,
  labelName: string = "label",
  valueName: string = "value"
) {
  const results: any[] = [];
  valArr.forEach(function (value: any) {
    value[labelName] = value[lableIndex];
    value[valueName] = value[valueIndex].toString();
    results.push(value);
  });
  return results;
}

function getStdCodesFromCountryList(countryList: any) {
  const codes: any[] = [];
  let existsCodes: any = [];
  countryList.forEach(function (value: any) {
    if (
      isNotEmpty(value["country_std_code"]) &&
      !existsCodes.includes(value["country_std_code"])
    ) {
      existsCodes.push(value["country_std_code"]);
      codes.push(value);
    }
  });
  return codes;
}

function getCurrenciesFromCountryList(countryList: any) {
  const currencies: any[] = [];
  let existsCurrencies: any = [];
  countryList.forEach(function (value: any) {
    if (
      isNotEmpty(value["currency_code"]) &&
      !existsCurrencies.includes(value["currency_code"])
    ) {
      existsCurrencies.push(value["currency_code"]);
      currencies.push(value);
    }
  });
  return currencies;
}

function processProductTypeFields(productTypeFields: any) {
  const processedFileds: any[] = [];
  let existsCurrencies: any = [];
  productTypeFields.forEach(function (pField: any) {
    processedFileds[pField["field_name"]] = pField;
  });
  return processedFileds;
}

function getFoodTypeLabel(foodType: string) {
  let foodTypeVal = "";
  foodTypeObjArr.forEach(function (foodTypeObj: any) {
    if (foodTypeObj.value == foodType) {
      foodTypeVal = foodTypeObj.label;
    }
  });
  return foodTypeVal;
}

function getSizeLabel(size: string) {
  let sizeVal = "";
  sizeObjArr.forEach(function (sizeObj: any) {
    if (sizeObj.value == size) {
      sizeVal = sizeObj.label;
    }
  });
  return sizeVal;
}

function getLabelFor(val: string, labelObjArr: any) {
  let labelVal = "";
  labelObjArr.forEach(function (labelObj: any) {
    if (labelObj.value == val) {
      labelVal = labelObj.label;
    }
  });
  return labelVal;
}

function getItemFor(val: any, item: any, objArr: any, matchWith = "id") {
  let itemVal = "";
  objArr.forEach(function (obj: any) {
    if (obj[matchWith] == val && obj[item]) {
      itemVal = obj[item];
    }
  });
  return itemVal;
}

function getPincodeForArea(areaId: string, areaObjArr: any) {
  let pincode = "";
  areaObjArr.forEach(function (areaObj: any) {
    if (areaObj.id == areaId && areaObj.pincode !== null) {
      pincode = areaObj.pincode;
    }
  });
  return pincode;
}

function getStdCodeForCountry(countyId: string, countryObjArr: any) {
  let stdCode = "";
  countryObjArr.forEach(function (cntObj: any) {
    if (cntObj.id == countyId && cntObj.country_std_code !== null) {
      stdCode = cntObj.country_std_code;
    }
  });
  return stdCode;
}

function getImageForTheme(
  themeId: string,
  themeObjArr: any,
  imageType: string = "preview_image_url"
) {
  let imageUrl = "";
  themeObjArr.forEach(function (thmObj: any) {
    if (thmObj.id == themeId && !isEmptyVal(thmObj[imageType])) {
      imageUrl = thmObj[imageType];
    }
  });
  return imageUrl;
}

function getAvailableDaysLabel(days: string) {
  let dayVal = "";

  if (isEmptyVal(days)) return dayVal;
  let daysArr = days.split(",");
  daysArr.forEach(function (day: any) {
    availableOnDayObjArr.forEach(function (dayObj: any) {
      if (dayObj.value == day) {
        dayVal = dayObj.label + " ";
      }
    });
  });
  return dayVal;
}

function getStatusLabel(status: any) {
  let statusVal = "";
  statusObjArr.forEach(function (statusObj: any) {
    if (statusObj.value == status) {
      statusVal = statusObj.label;
    }
  });
  return statusVal;
}

function getHourMinuteFormat(timeVal: string) {
  const timeArr = timeVal.split(":");
  let timeToReturn: string = "";
  if (!isEmptyVal(timeArr[0])) timeToReturn = timeArr[0];
  if (!isEmptyVal(timeArr[1])) timeToReturn = timeToReturn + ":" + timeArr[1];
  return timeToReturn;
}

function getMenuIcon(iconName: string) {
  let icon = null;
  if (!iconName) return icon;
  switch (iconName.toLowerCase()) {
    case "dashboard":
    case "home":
      icon = <Dashboard />;
      break;
    case "song_library":
      icon = <MediaIcon />;
      break;
    case "commercials":
      icon = <PeopleSpeakerIcon />;
      break;
    case "reports":
      icon = <DetailIcon />;
      break;
    case "charts":
    case "public_charts":
      icon = <TextImageIcon />;
      break;
    case "commercial_schedules":
      icon = <EventDetailIcon />;
      break;
    case "music_schedules":
      icon = <CalendarIcon />;
      break;
    case "music_master":
      icon = <PlayOutlineIcon />;
      break;
    default:
      icon = <OthersIcon />;
      break;
  }
  return icon;
}

function timeStringToMinutes(timeStr) {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

function compareTimes(time1, time2) {
  const minutes1 = timeStringToMinutes(time1);
  const minutes2 = timeStringToMinutes(time2);

  if (minutes1 > minutes2) return 1;
  if (minutes1 < minutes2) return -1;
  if (minutes1 == minutes2) return 0;
}

function dbDateToCalender(inputDate: any) {
  let date = inputDate.replace(/[-]/g, "/");
  // parse the proper date string from the formatted string.
  date = Date.parse(date);
  // create new date
  return new Date(date);
}

function calenderDateToDB(inputDate: any) {
  //parse the input date
  const date = new Date(inputDate);

  //extract the parts of the date
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  let format = "yyyy-MM-DD";
  //replace the month
  format = format.replace("MM", month.toString().padStart(2, "0"));

  //replace the year
  if (format.indexOf("yyyy") > -1) {
    format = format.replace("yyyy", year.toString());
  } else if (format.indexOf("yy") > -1) {
    format = format.replace("yy", year.toString().substr(2, 2));
  }

  //replace the day
  format = format.replace("DD", day.toString().padStart(2, "0"));

  return format;
}

function genreTitle(rData: any) {
  let gData = "";
  if (!isEmptyVal(rData.genre_title)) gData += rData.genre_title;
  if (!isEmptyVal(rData.sub_genre_title))
    gData += " (" + rData.sub_genre_title + ")";

  return gData;
}

function getPrimaryAddress(respData: any) {
  let PrimeAddress = respData.addresses.find(
    (address: any) => address.is_primary_address == 1
  );
  return PrimeAddress;
}

function dateFormat(inputDate: any, format: string) {
  //parse the input date
  const date = new Date(inputDate);

  //extract the parts of the date
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  //replace the month
  format = format.replace("MM", month.toString().padStart(2, "0"));

  //replace the year
  if (format.indexOf("yyyy") > -1) {
    format = format.replace("yyyy", year.toString());
  } else if (format.indexOf("yy") > -1) {
    format = format.replace("yy", year.toString().substr(2, 2));
  }

  //replace the day
  format = format.replace("dd", day.toString().padStart(2, "0"));

  return format;
}

function isWebResolutionSize() {
  return window.innerWidth > 768;
}

function getDaysDuration() {
  let daysDuration = [...orderDayTypeObj.all.daysOptions];
}

function isAlpha(value: string) {
  return value && IS_ALPHA_REGEX.test(value);
}

function isAlphaNumeric(value: string) {
  return value && IS_ALPHANUMERIC_REGEX.test(value);
}

function isAlphaNumericWithSpace(value: string) {
  return value && IS_ALPHANUMERIC_WITH_SPACE_REGEX.test(value);
}

function isAlphaNumericWithDash(value: string) {
  return value && IS_ALPHANUMERIC_WITH_DASH_REGEX.test(value);
}

function isAlphaNumericDashSpace(value: string) {
  return value && IS_ALPHANUMERIC_WITH_DASH_SPACE_REGEX.test(value);
}

function isAlphaNumericWithSpecialChars(
  value: string,
  specialCharsArray: string[]
) {
  let specialChars = specialCharsArray.join("");
  const regex = new RegExp(
    "^(?!\\d+$)(?:[a-zA-Z0-9][a-zA-Z0-9" + specialChars + "]*)?$"
  );
  return value && value.match(regex);
}

function isAlphaWithSpecialChars(value: string, specialCharsArray: string[]) {
  let specialChars = specialCharsArray.join("");
  const regex = new RegExp(
    "^(?!\\d+$)(?:[a-zA-Z][a-zA-Z" + specialChars + "]*)?$"
  );
  return value && value.match(regex);
}

export {
  isEmptyVal,
  getMenuIcon,
  genreTitle,
  isNotEmpty,
  displayField,
  countCommaSeparatedVals,
  displayChartCompany,
  displayAmountField,
  displayDateField,
  displayDateTimeField,
  formatTimeField,
  getTodayShortDay,
  generateTimeOptions,
  getDateShortDay,
  displayTimeField,
  displayDayDateField,
  displayDateMonthField,
  displayDayDateTimeField,
  processMinute,
  processSecond,
  displayYesNo,
  addLabelValue,
  isWebResolutionSize,
  getCurrenciesFromCountryList,
  getStdCodesFromCountryList,
  isAlpha,
  isAlphaNumeric,
  getImageForTheme,
  isAlphaNumericWithSpace,
  isAlphaNumericWithDash,
  isAlphaNumericDashSpace,
  isAlphaNumericWithSpecialChars,
  isAlphaWithSpecialChars,
  getFoodTypeLabel,
  getStatusLabel,
  getSizeLabel,
  getAvailableDaysLabel,
  processProductTypeFields,
  isNotNull,
  dateFormat,
  getHourMinuteFormat,
  dbDateToCalender,
  calenderDateToDB,
  getPrimaryAddress,
  getLabelFor,
  getItemFor,
  getDaysDuration,
  compareTimes,
  getPincodeForArea,
  getStdCodeForCountry,
  zeroFill,
  removeHTMLTags,
  isValidURL,
  getBasename,
};
